/**
 * Markdown parsing utilities for TOC generation
 */

export interface TocItem {
  id: string;
  text: string;
  level: number; // 2 for H2, 3 for H3, etc.
}

/**
 * Parse markdown content and extract headers (H2, H3) for TOC
 */
export function parseMarkdownHeaders(markdown: string): TocItem[] {
  if (!markdown) return [];

  const headers: TocItem[] = [];
  const lines = markdown.split('\n');
  const idCounts = new Map<string, number>(); // Track duplicate IDs

  for (const line of lines) {
    // Match H2 (##) and H3 (###) headers
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (match) {
      const level = match[1].length; // 2 or 3
      const text = match[2].trim();

      // Generate a slug-like ID from the header text
      let id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special chars
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim();

      // Make ID unique if it's a duplicate
      if (idCounts.has(id)) {
        const count = idCounts.get(id)! + 1;
        idCounts.set(id, count);
        id = `${id}-${count}`;
      } else {
        idCounts.set(id, 1);
      }

      headers.push({
        id,
        text,
        level,
      });
    }
  }

  return headers;
}

/**
 * Scroll to a specific header ID with smooth behavior
 */
export function scrollToHeader(headerId: string) {
  const element = document.getElementById(headerId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
